import apiService from './api'

const MEDIA_BASE_URL = (import.meta.env.VITE_MEDIA_BASE_URL as string)?.replace(/\/$/, '') || ''

export interface SignedUrlResponse {
  uploadUrl: string
  fileKey: string
  cloudFrontUrl: string
}

export type MediaCategory = 'logo' | 'coverImage' | 'gallery' | 'introVideo' | 'videoGallery' | 'courseImage' | 'courseVideo' | 'generalImage' | 'generalVideo'

class MediaService {
  /**
   * Build display URL from file key. Store keys in data; use this when displaying.
   * If value is already a full URL (http), returns as-is.
   */
  getMediaUrlFromKey(keyOrUrl: string): string {
    if (!keyOrUrl) return ''
    if (keyOrUrl.startsWith('http://') || keyOrUrl.startsWith('https://')) return keyOrUrl
    if (!MEDIA_BASE_URL) return keyOrUrl
    return `${MEDIA_BASE_URL}/${keyOrUrl}`
  }

  /**
   * Get upload URL from backend (presigned PUT); then frontend uploads file to storage.
   */
  async getUploadUrl(
    fileName: string,
    fileType: string,
    fileSize: number,
    category: MediaCategory,
    schoolId?: string,
    courseId?: string
  ): Promise<{ success: boolean; data: SignedUrlResponse }> {
    try {
      const payload: any = {
        fileName,
        fileType,
        fileSize,
        category
      }
      
      // Only include schoolId and courseId if provided
      if (schoolId) payload.schoolId = schoolId
      if (courseId) payload.courseId = courseId
      
      const response = await apiService.getMediaUploadUrl(payload)
      return response
    } catch (error) {
      console.error('Error getting upload URL:', error)
      throw error
    }
  }

  /**
   * Get media URL from API (only needed when you don't have VITE_MEDIA_BASE_URL).
   */
  async getMediaUrl(fileKey: string, expiresIn: number = 3600): Promise<{ success: boolean; data: { url: string; fileKey: string; expiresIn: number } }> {
    try {
      const response = await apiService.getMediaUrl({ fileKey, expiresIn })
      return response
    } catch (error) {
      console.error('Error getting media URL:', error)
      throw error
    }
  }

  /**
   * Batch get media URLs from API (only needed when you don't have VITE_MEDIA_BASE_URL).
   */
  async getBatchMediaUrls(fileKeys: string[], expiresIn: number = 3600): Promise<{ success: boolean; data: { urls: Array<{ fileKey: string; url: string }>; expiresIn: number } }> {
    try {
      const response = await apiService.getBatchMediaUrls({ fileKeys, expiresIn })
      return response
    } catch (error) {
      console.error('Error getting batch media URLs:', error)
      throw error
    }
  }

  /**
   * Upload file directly to S3 using signed URL with progress tracking
   */
  async uploadFileToS3(
    file: File,
    uploadUrl: string,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const progress = Math.round((e.loaded * 100) / e.total)
          onProgress(progress)
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          resolve()
        } else {
          reject(new Error(`Upload failed: ${xhr.statusText}`))
        }
      })

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed: Network error'))
      })

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'))
      })

      xhr.open('PUT', uploadUrl)
      xhr.setRequestHeader('Content-Type', file.type)
      xhr.send(file)
    })
  }

  /**
   * Upload single file using signed URL flow.
   * Returns fileKey — store this in the entity (school/course/room). Display with getMediaUrlFromKey(fileKey).
   */
  async uploadFile(
    file: File,
    category: MediaCategory,
    schoolId?: string,
    courseId?: string,
    onProgress?: (progress: number) => void
  ): Promise<{ success: boolean; data: { fileKey: string; cloudFrontUrl: string } }> {
    try {
      const { data: signedUrlData } = await this.getUploadUrl(
        file.name,
        file.type,
        file.size,
        category,
        schoolId,
        courseId
      )
      await this.uploadFileToS3(file, signedUrlData.uploadUrl, onProgress)
      return {
        success: true,
        data: {
          fileKey: signedUrlData.fileKey,
          cloudFrontUrl: signedUrlData.cloudFrontUrl
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error)
      throw error
    }
  }

  /**
   * Delete media file from S3 and school
   */
  async deleteFile(fileKey: string, category: MediaCategory): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiService.delete('/schools/media', {
        data: { fileKey, category }
      })
      return response
    } catch (error) {
      console.error('Error deleting file:', error)
      throw error
    }
  }

  /**
   * Extract a file key from either a stored key or a public media URL.
   */
  extractFileKeyFromUrl(value: string): string | null {
    try {
      if (!value) return null

      // Already a stored file key.
      if (!value.startsWith('http://') && !value.startsWith('https://')) {
        return value
      }

      const parsedUrl = new URL(value)
      const pathname = parsedUrl.pathname.startsWith('/')
        ? parsedUrl.pathname.slice(1)
        : parsedUrl.pathname

      return pathname || null
    } catch (error) {
      console.error('Error extracting fileKey from URL:', error)
      return null
    }
  }

  /**
   * Get school profile to fetch media
   */
  async getSchoolMedia(): Promise<{ 
    images: { logo?: string; coverImage?: string; gallery: string[] }
    videos: { introVideo?: string; gallery: string[] }
  }> {
    try {
      const response = await apiService.getSchoolProfile()
      
      if (response.success && response.data) {
        // Handle different response structures
        let school = null
        
        // Check for nested school object: { data: { school: {...} } }
        if (response.data.school && response.data.school._id) {
          school = response.data.school
        }
        // Check for array of schools: { data: { schools: [...] } }
        else if (response.data.schools && Array.isArray(response.data.schools) && response.data.schools.length > 0) {
          school = response.data.schools[0]
        }
        // Check for direct school object: { data: { _id: ..., ... } }
        else if (response.data._id) {
          school = response.data
        }
        
        if (school) {
          return {
            images: {
              logo: school.images?.logo || undefined,
              coverImage: school.images?.coverImage || undefined,
              gallery: school.images?.gallery || []
            },
            videos: {
              introVideo: school.videos?.introVideo || undefined,
              gallery: school.videos?.gallery || []
            }
          }
        }
      }
      return { images: { gallery: [] }, videos: { gallery: [] } }
    } catch (error) {
      console.error('Error fetching school media:', error)
      return { images: { gallery: [] }, videos: { gallery: [] } }
    }
  }

  /**
   * Validate file before upload
   */
  validateFile(file: File, category: MediaCategory): { valid: boolean; error?: string } {
    const isImage = category === 'logo' || category === 'coverImage' || category === 'gallery'
    const isVideo = category === 'introVideo' || category === 'videoGallery'
    
    const maxSize = isImage ? 10 * 1024 * 1024 : 100 * 1024 * 1024 // 10MB for images, 100MB for videos
    
    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File size must be less than ${isImage ? '10MB' : '100MB'}`
      }
    }

    if (isImage) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
      if (!allowedTypes.includes(file.type.toLowerCase())) {
        return {
          valid: false,
          error: 'Only JPEG, PNG, WebP, and GIF images are allowed'
        }
      }
    } else if (isVideo) {
      const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo']
      if (!allowedTypes.includes(file.type.toLowerCase())) {
        return {
          valid: false,
          error: 'Only MP4, WebM, and QuickTime videos are allowed'
        }
      }
    }

    return { valid: true }
  }
}

export default new MediaService()
